import React from "react";
import ReactDOM from "react-dom";
import * as AlfredoStyles from "../../../styles/components/configuration/modals/CompanyModal";
import * as AlfredoComponents from "../..";
import { useModalAcessivel } from "../../../hooks/useModalAcessivel";

interface CompanyModalProps {
  selectedObject: any;
  selectedInfo: any;
  handleCloseModal: () => void;
  handleSave: (objectType: any) => void;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => void;
  setSelectedInfo: (info: any) => void;
  setTempFormData: (data: any) => void;
  parseInfoValue: (infoValue: string) => any;
  decryptedInfoToken: any;
  handleOpenModal: (type: string, object?: any) => void;
}

const CompanyModal: React.FC<CompanyModalProps> = ({
  selectedObject,
  selectedInfo,
  handleCloseModal,
  handleSave,
  handleInputChange,
  setSelectedInfo,
  setTempFormData,
  parseInfoValue,
  decryptedInfoToken,
  handleOpenModal,
}) => {
  // Esc fecha, o foco fica preso dentro e volta para quem abriu, e o leitor
  // de tela anuncia a janela pelo titulo. Ver hooks/useModalAcessivel.ts.
  const { refDoModal, propsDoDialogo } = useModalAcessivel({
    aoFechar: handleCloseModal,
    idDoTitulo: "titulo-companymodal",
  });

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatUser = (userId: string) => {
    const USER_MAP: { [key: string]: string } = {
      "e16904f8-1d9b-4579-907c-2cf5dcc9979f": "Administrador",
      default: "Usuário do Sistema",
    };
    return USER_MAP[userId] || USER_MAP.default;
  };

  return ReactDOM.createPortal(
    <AlfredoStyles.ModalOverlay>
      <AlfredoStyles.ModalContent ref={refDoModal} {...propsDoDialogo} large>
        <AlfredoStyles.ModalHeader>
          <AlfredoStyles.ModalTitle id="titulo-companymodal">
            <AlfredoStyles.MaterialIcons>business</AlfredoStyles.MaterialIcons>
            Empresa - {selectedObject?.companyName || "Nova Empresa"}
          </AlfredoStyles.ModalTitle>
          <AlfredoStyles.ModalCloseButton onClick={handleCloseModal}>
            <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
          </AlfredoStyles.ModalCloseButton>
        </AlfredoStyles.ModalHeader>

        <AlfredoStyles.ModalBody>
          <AlfredoStyles.CompanyContainer>
            {/* Informações Principais */}
            <AlfredoStyles.CompanyTopContainer>
              <AlfredoStyles.FormSection>
                <AlfredoStyles.SectionTitle>
                  Informações da Empresa
                </AlfredoStyles.SectionTitle>
                <AlfredoStyles.FormRow>
                  <AlfredoStyles.LabelInputContainer>
                    <AlfredoStyles.Label>
                      <AlfredoStyles.MaterialIcons>
                        badge
                      </AlfredoStyles.MaterialIcons>
                      Nome do Estabelecimento:
                    </AlfredoStyles.Label>
                    <AlfredoStyles.Input
                      type="text"
                      value={selectedObject.companyName || ""}
                      onChange={(e) => handleInputChange(e, "companyName")}
                      placeholder="Digite o nome da empresa"
                    />
                  </AlfredoStyles.LabelInputContainer>

                  <AlfredoStyles.LabelInputContainer>
                    <AlfredoStyles.Label>
                      <AlfredoStyles.MaterialIcons>
                        description
                      </AlfredoStyles.MaterialIcons>
                      Descrição:
                    </AlfredoStyles.Label>
                    <AlfredoStyles.Input
                      type="text"
                      value={selectedObject.companyDescription || ""}
                      onChange={(e) =>
                        handleInputChange(e, "companyDescription")
                      }
                      placeholder="Descrição da empresa"
                    />
                  </AlfredoStyles.LabelInputContainer>
                </AlfredoStyles.FormRow>

                <AlfredoStyles.FormRow>
                  <AlfredoStyles.LabelInputContainer>
                    <AlfredoStyles.Label>
                      <AlfredoStyles.MaterialIcons>
                        fingerprint
                      </AlfredoStyles.MaterialIcons>
                      CNPJ:
                    </AlfredoStyles.Label>
                    <AlfredoStyles.Input
                      type="text"
                      value={selectedObject.companyTaxPayerID || ""}
                      onChange={(e) =>
                        handleInputChange(e, "companyTaxPayerID")
                      }
                      placeholder="00.000.000/0000-00"
                    />
                  </AlfredoStyles.LabelInputContainer>

                  <AlfredoStyles.LabelInputContainer>
                    <AlfredoStyles.Label>
                      <AlfredoStyles.MaterialIcons>
                        phone
                      </AlfredoStyles.MaterialIcons>
                      Telefone:
                    </AlfredoStyles.Label>
                    <AlfredoStyles.Input
                      type="text"
                      value={selectedObject.companyPhone || ""}
                      onChange={(e) => handleInputChange(e, "companyPhone")}
                      placeholder="(00) 00000-0000"
                    />
                  </AlfredoStyles.LabelInputContainer>
                </AlfredoStyles.FormRow>
              </AlfredoStyles.FormSection>

              {/* Metadados */}
              <AlfredoStyles.FormSection>
                <AlfredoStyles.SectionTitle>
                  Informações do Sistema
                </AlfredoStyles.SectionTitle>
                <AlfredoStyles.InfoGrid>
                  <AlfredoStyles.InfoItem>
                    <AlfredoStyles.InfoLabel>
                      <AlfredoStyles.MaterialIcons>
                        date_range
                      </AlfredoStyles.MaterialIcons>
                      Criado em:
                    </AlfredoStyles.InfoLabel>
                    <AlfredoStyles.InfoValue>
                      {formatDate(selectedObject.createdAt)}
                    </AlfredoStyles.InfoValue>
                  </AlfredoStyles.InfoItem>

                  <AlfredoStyles.InfoItem>
                    <AlfredoStyles.InfoLabel>
                      <AlfredoStyles.MaterialIcons>
                        update
                      </AlfredoStyles.MaterialIcons>
                      Última atualização:
                    </AlfredoStyles.InfoLabel>
                    <AlfredoStyles.InfoValue>
                      {formatDate(selectedObject.updatedAt)}
                    </AlfredoStyles.InfoValue>
                  </AlfredoStyles.InfoItem>

                  <AlfredoStyles.InfoItem>
                    <AlfredoStyles.InfoLabel>
                      <AlfredoStyles.MaterialIcons>
                        person
                      </AlfredoStyles.MaterialIcons>
                      Alterado por:
                    </AlfredoStyles.InfoLabel>
                    <AlfredoStyles.InfoValue>
                      {formatUser(selectedObject.updatedBy)}
                    </AlfredoStyles.InfoValue>
                  </AlfredoStyles.InfoItem>

                  <AlfredoStyles.InfoItem>
                    <AlfredoStyles.InfoLabel>
                      <AlfredoStyles.MaterialIcons>
                        security
                      </AlfredoStyles.MaterialIcons>
                      Status:
                    </AlfredoStyles.InfoLabel>
                    <AlfredoStyles.InfoValue
                      status={selectedObject.companyStatus}
                    >
                      {selectedObject.companyStatus ? "Ativo" : "Inativo"}
                    </AlfredoStyles.InfoValue>
                  </AlfredoStyles.InfoItem>
                </AlfredoStyles.InfoGrid>
              </AlfredoStyles.FormSection>
            </AlfredoStyles.CompanyTopContainer>

            {/* Navegação e Conteúdo Dinâmico */}
            <AlfredoStyles.CompanyBottom>
              <AlfredoStyles.CompanyInfoContainer>
                <AlfredoStyles.CompanyInfoList>
                  <AlfredoStyles.CompanyInfo
                    selected={selectedInfo?.companyInfoType === 1}
                    onClick={() => {
                      setSelectedInfo({ companyInfoType: 1 });
                      setTempFormData(null);
                    }}
                  >
                    <AlfredoStyles.StyledIcon>
                      business
                    </AlfredoStyles.StyledIcon>
                    <div>
                      <div>Informações Gerais</div>
                      <small>Dados básicos da empresa</small>
                    </div>
                  </AlfredoStyles.CompanyInfo>

                  <AlfredoStyles.CompanyInfo
                    selected={selectedInfo?.companyInfoType === 2}
                    onClick={() => {
                      setSelectedInfo({ companyInfoType: 2 });
                      setTempFormData(null);
                    }}
                  >
                    <AlfredoStyles.StyledIcon>
                      category
                    </AlfredoStyles.StyledIcon>
                    <div>
                      <div>Categorias de Produtos</div>
                      <small>Gerencie categorias</small>
                    </div>
                  </AlfredoStyles.CompanyInfo>

                  <AlfredoStyles.CompanyInfo
                    selected={selectedInfo?.companyInfoType === 3}
                    onClick={() => {
                      setSelectedInfo({ companyInfoType: 3 });
                      setTempFormData(null);
                    }}
                  >
                    <AlfredoStyles.StyledIcon>notes</AlfredoStyles.StyledIcon>
                    <div>
                      <div>Observações</div>
                      <small>Anotações internas</small>
                    </div>
                  </AlfredoStyles.CompanyInfo>

                  <AlfredoStyles.CompanyInfo
                    selected={selectedInfo?.companyInfoType === 4}
                    onClick={() => {
                      setSelectedInfo({ companyInfoType: 4 });
                      setTempFormData(null);
                    }}
                  >
                    <AlfredoStyles.StyledIcon>
                      local_offer
                    </AlfredoStyles.StyledIcon>
                    <div>
                      <div>Descontos</div>
                      <small>Políticas de desconto</small>
                    </div>
                  </AlfredoStyles.CompanyInfo>

                  <AlfredoStyles.CompanyInfo
                    selected={selectedInfo?.companyInfoType === 5}
                    onClick={() => {
                      setSelectedInfo({ companyInfoType: 5 });
                      setTempFormData(null);
                    }}
                  >
                    <AlfredoStyles.StyledIcon>
                      payments
                    </AlfredoStyles.StyledIcon>
                    <div>
                      <div>Formas de Pagamento</div>
                      <small>Métodos aceitos</small>
                    </div>
                  </AlfredoStyles.CompanyInfo>
                </AlfredoStyles.CompanyInfoList>

                <AlfredoStyles.CompanyInfoContent>
                  {selectedInfo?.companyInfoType === 1 && (
                    <AlfredoStyles.InfoSection>
                      <AlfredoStyles.SectionTitle>
                        Informações Gerais da Empresa
                      </AlfredoStyles.SectionTitle>
                      <AlfredoStyles.FormGrid>
                        <AlfredoStyles.LabelInputContainer>
                          <AlfredoStyles.Label>Email:</AlfredoStyles.Label>
                          <AlfredoStyles.Input
                            type="email"
                            value={selectedObject.companyEmail || ""}
                            onChange={(e) =>
                              handleInputChange(e, "companyEmail")
                            }
                            placeholder="empresa@exemplo.com"
                          />
                        </AlfredoStyles.LabelInputContainer>

                        <AlfredoStyles.LabelInputContainer>
                          <AlfredoStyles.Label>Endereço:</AlfredoStyles.Label>
                          <AlfredoStyles.Input
                            type="text"
                            value={selectedObject.companyAddress || ""}
                            onChange={(e) =>
                              handleInputChange(e, "companyAddress")
                            }
                            placeholder="Endereço completo"
                          />
                        </AlfredoStyles.LabelInputContainer>

                        <AlfredoStyles.LabelInputContainer>
                          <AlfredoStyles.Label>Cidade:</AlfredoStyles.Label>
                          <AlfredoStyles.Input
                            type="text"
                            value={selectedObject.companyCity || ""}
                            onChange={(e) =>
                              handleInputChange(e, "companyCity")
                            }
                            placeholder="Cidade"
                          />
                        </AlfredoStyles.LabelInputContainer>

                        <AlfredoStyles.LabelInputContainer>
                          <AlfredoStyles.Label>Estado:</AlfredoStyles.Label>
                          <AlfredoStyles.Input
                            type="text"
                            value={selectedObject.companyState || ""}
                            onChange={(e) =>
                              handleInputChange(e, "companyState")
                            }
                            placeholder="UF"
                          />
                        </AlfredoStyles.LabelInputContainer>
                      </AlfredoStyles.FormGrid>
                    </AlfredoStyles.InfoSection>
                  )}

                  {selectedInfo?.companyInfoType === 2 && (
                    <AlfredoStyles.InfoSection>
                      <AlfredoStyles.SectionTitle>
                        Categorias de Produtos
                      </AlfredoStyles.SectionTitle>
                      <AlfredoComponents.DynamicTable
                        tableType={2}
                        handleOpenModal={handleOpenModal}
                      />
                    </AlfredoStyles.InfoSection>
                  )}

                  {selectedInfo?.companyInfoType === 3 && (
                    <AlfredoStyles.InfoSection>
                      <AlfredoStyles.SectionTitle>
                        Observações Internas
                      </AlfredoStyles.SectionTitle>
                      <AlfredoStyles.TextArea
                        as="textarea"
                        value={selectedObject.subsidiaryNotes || ""}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                          handleInputChange(e, "subsidiaryNotes")
                        }
                        placeholder="Anotações importantes sobre a empresa..."
                        rows={6}
                      />
                    </AlfredoStyles.InfoSection>
                  )}

                  {selectedInfo?.companyInfoType === 4 && (
                    <AlfredoStyles.InfoSection>
                      <AlfredoStyles.SectionTitle>
                        Políticas de Desconto
                      </AlfredoStyles.SectionTitle>
                      <AlfredoStyles.AlertMessage type="info">
                        <AlfredoStyles.MaterialIcons>
                          info
                        </AlfredoStyles.MaterialIcons>
                        Configure as políticas de desconto para esta empresa.
                      </AlfredoStyles.AlertMessage>
                    </AlfredoStyles.InfoSection>
                  )}

                  {selectedInfo?.companyInfoType === 5 && (
                    <AlfredoStyles.InfoSection>
                      <AlfredoStyles.SectionTitle>
                        Formas de Pagamento
                      </AlfredoStyles.SectionTitle>
                      <AlfredoStyles.AlertMessage type="info">
                        <AlfredoStyles.MaterialIcons>
                          info
                        </AlfredoStyles.MaterialIcons>
                        Configure os métodos de pagamento aceitos por esta
                        empresa.
                      </AlfredoStyles.AlertMessage>
                    </AlfredoStyles.InfoSection>
                  )}
                </AlfredoStyles.CompanyInfoContent>
              </AlfredoStyles.CompanyInfoContainer>
            </AlfredoStyles.CompanyBottom>
          </AlfredoStyles.CompanyContainer>
        </AlfredoStyles.ModalBody>

        <AlfredoStyles.ModalFooter>
          <AlfredoStyles.ButtonGroup>
            <AlfredoStyles.SecondaryButton onClick={handleCloseModal}>
              <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
              Cancelar
            </AlfredoStyles.SecondaryButton>
            <AlfredoStyles.PrimaryButton onClick={() => handleSave("company")}>
              <AlfredoStyles.MaterialIcons>save</AlfredoStyles.MaterialIcons>
              Salvar Alterações
            </AlfredoStyles.PrimaryButton>
          </AlfredoStyles.ButtonGroup>
        </AlfredoStyles.ModalFooter>
      </AlfredoStyles.ModalContent>
    </AlfredoStyles.ModalOverlay>,
    document.body
  );
};

export default CompanyModal;
